import React, { useCallback, useEffect, useMemo } from 'react';
import { Layout, Row, Col, Space, Card, Spin, Tabs } from 'antd';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTickerSearchIndex } from "../../modules/Search/useTickerSearchIndex";
import { useTickerData } from "../../hooks/useTickerData";
import { selectTickerMetadata } from "../../store/slices/globalResources.slice";
import TickerBanner from "./TickerBanner";
import EtfDetails from "./etf/EtfDetails";
import StockFinancials from "./StockFinancials";
import SectorIndustryChart from "../../components/Charts/SectorIndustryChart";
import TradingViewChart from "../../charts/TradingViewChart";
const COMMON_FIELDS = [
    'symbol', 'name', 'logo', 'tags', 'metrics'
];

const ETF_FIELDS = [
    'holdings', 'specs', 'composite.sector_gic', 'composite.industry_gic'
];

const STOCK_FIELDS = [
    // Removed 'financials' since it comes from a different endpoint
];

const getFields = (type) => [
    ...COMMON_FIELDS,
    ...(type === 'stock' ? STOCK_FIELDS : ETF_FIELDS)
];


const arrayToObj = (arr) => {
    const obj = {};
    Array.from(Object.values(arr || {})).forEach(x => {
        obj[x.k] = x.v;
    });
    return obj;
};

// Tab configurations for different security types
const TAB_CONFIGS = {
    stock: [
        {
            key: 'overview',
            label: 'Overview',
            component: ({ ticker }) => (
                <Card>
                    <TradingViewChart symbol={ticker.symbol} />
                </Card>
            )
        },
        {
            key: 'financials',
            label: 'Financials',
            component: ({ ticker }) => (
                <StockFinancials symbol={ticker.symbol} />
            )
        }
    ],
    etf: [
        {
            key: 'overview',
            label: 'Overview',
            component: ({ ticker }) => (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Card>
                        <TradingViewChart symbol={ticker.symbol} />
                    </Card>
                    <EtfDetails etf={ticker} />
                </Space>
            )
        },
        {
            key: 'composition',
            label: 'Composition',
            component: ({ ticker }) => (
                <Card>
                    <Space direction="vertical" style={{ maxHeight: 400 }}>
                        <SectorIndustryChart
                            sectors={arrayToObj(ticker?.composite?.sector_gic)}
                            industries={arrayToObj(ticker?.composite?.industry_gic)}
                            height={300}
                            width={500}
                            treeChartProps={{ labelSize: 9 }}
                        />
                    </Space>
                </Card>
            )
        },
        {
            key: 'holdings',
            label: 'Holdings',
            component: ({ ticker }) => (
                <Card>
                    {/* Add Holdings component here */}
                </Card>
            )
        }
    ]
};

const TickerView = () => {
    const { ticker: urlTicker } = useParams();
    const symbol = urlTicker || 'spy';

    const { tickers } = useTickerSearchIndex([symbol]);
    const tickerMetadata = useSelector((state) => selectTickerMetadata(state, symbol));
    const fields = getFields(tickerMetadata?.type ?? 'etf');
    const tickerData = useTickerData([symbol], fields, 'US');

    const activeTabConfig = useMemo(() => {
        const type = tickerMetadata?.type || 'etf';
        return TAB_CONFIGS[type] || TAB_CONFIGS.etf;
    }, [tickerMetadata?.type]);

    if (!tickerMetadata?.type || !tickerData?.[symbol]) {
        return (
            <Card>
                <Spin size="large" /> Loading {symbol}
            </Card>
        );
    }

    return (
        <Layout>
            <TickerBanner ticker={tickerData[symbol]} />
            <Card>
                <Tabs
                    defaultActiveKey="overview"
                    items={activeTabConfig.map(tab => ({
                        key: tab.key,
                        label: tab.label,
                        children: <tab.component ticker={tickerData[symbol]} />
                    }))}
                />
            </Card>
        </Layout>
    );
};

export default TickerView;